/**
 * @file: cube信息查询
 * @author: xiaolong.huang@dmall.com
 */

import Config from '../config/index';
import Utils from '../utils/util';
import {
    findByCubeId,
    findParentCubeByCubeId,
    getChildCubeIds,
    findParentIdByCubeId,
    getCubeByType,
    findChildCubeIds,
    findBrothersContainSelfByCubeId,
    getCubeIndexInParent
} from './baseQuery';
import { getCubeType } from './cubeType';

export function cubeRelationType(cubeList: Array<CubeId>): string {
    let cubeTypes: Array<CubeType|null> = [];

    cubeList.forEach((cubeId: CubeId): void => {
        let cube: CubeNode = findByCubeId(cubeId);

        cubeTypes.push(getCubeType(cube));
    });

    return cubeTypes.join('-');
}

// 获取cube的父级id
export function getCubeParentId(cubeId: CubeId): CubeId {
    let cube: CubeNode = findByCubeId(cubeId);

    return cube.parentId;
}

// 获取cube的索引值
export function getCubeIndex(cubeId: CubeId): number {
    let parentCube: CubeNode = findParentCubeByCubeId(cubeId);
    let childIds: Array<CubeId> = getChildCubeIds(parentCube.id);

    for (let i: number = 0; i < childIds.length; i++) {
        if (childIds[i] === cubeId) {
            return i;
        }
    }

    return -1;
}

// 获取目标cube的子级cube数目
export function getChildCubeCount(cubeId: CubeId): number {
    return getChildCubeIds(cubeId).length;
}

// 是其父级的最后一个cube
export function isLastChild(cubeId: CubeId): boolean {
    let cubeIndex: number = getCubeIndex(cubeId);
    let bothers: Array<CubeId> = findBrothersContainSelfByCubeId(cubeId);

    return cubeIndex === bothers.length - 1;
}

// 最后一个货架
export function isLastShelf(cubeId: CubeId): boolean {
    let result: boolean = false;
    let parentId: CubeId = findParentIdByCubeId(cubeId);
    let childIds: Array<CubeId> = getChildCubeIds(parentId);

    for (let i: number = 0; i < childIds.length; i++) {
        if (childIds[i] === cubeId) {
            result = true;
            continue;
        }

        if (isShelf(childIds[i])) {
            result = false;
        }
    }

    return result;
}

export function listHasShelf(cubeIds: CubeId[]): boolean {
    for (let i = 0; i < cubeIds.length; i++) {
        if (isShelf(cubeIds[i]) || isFreezer(cubeIds[i])) {
            return true;
        }
    }

    return false;
}

// 第一个货架
export function isFirstShelf(cubeId: CubeId): boolean {
    let parentId: CubeId = findParentIdByCubeId(cubeId);
    let childIds: Array<CubeId> = getChildCubeIds(parentId);

    if (!isShelf(cubeId)) {
        return false;
    }

    for (let i: number = 0; i < childIds.length; i++) {
        if (isShelf(childIds[i])) {
            return childIds[i] === cubeId;
        }
    }

    return false;
}

// 第一个卧柜
export function isFirstFreezer(cubeId: CubeId): boolean {
    let parentId: CubeId = findParentIdByCubeId(cubeId);
    let childIds: Array<CubeId> = getChildCubeIds(parentId);

    if (!isFreezer(cubeId)) {
        return false;
    }

    for (let i: number = 0; i < childIds.length; i++) {
        if (isFreezer(childIds[i])) {
            return childIds[i] === cubeId;
        }
    }

    return false;
}

export function isFirstCube(cubeId: CubeId): boolean {
    let index: number = getCubeIndexInParent(cubeId);

    return index === 0;
}

export function isLastCube(cubeId: CubeId): boolean {
    let index: number = getCubeIndexInParent(cubeId);
    let brothers: Array<CubeId> = findBrothersContainSelfByCubeId(cubeId);

    return index === brothers.length - 1;
}

// 最后一个卧柜
export function isLastFreezer(cubeId: CubeId): boolean {
    let result: boolean = false;
    let parentId: CubeId = findParentIdByCubeId(cubeId);
    let childIds: Array<CubeId> = getChildCubeIds(parentId);

    for (let i: number = 0; i < childIds.length; i++) {
        if (childIds[i] === cubeId) {
            result = true;
            continue;
        }

        if (isFreezer(childIds[i])) {
            result = false;
        }
    }

    return result;
}

export function isEmptyFreezer(freezer: CubeId): boolean {
    let freezerCube: CubeNode = findByCubeId(freezer);

    if (!isFreezer(freezer)) {
        return false;
    }

    if (!Utils.isArray(freezerCube.cubes)) {
        return true;
    }

    if (freezerCube.cubes.length === 0) {
        return true;
    }

    if (!Utils.isArray(freezerCube.cubes[0].cubes)) {
        return true;
    }

    return freezerCube.cubes[0].cubes.length === 0;
}

// cube是否在画布中
export function isInStage(cubeId: CubeId): boolean {
    let parentId: CubeId = findParentIdByCubeId(cubeId);

    return isSketchpad(parentId);
}

// 在层板中
export function isInLayer(cubeId: CubeId): boolean {
    let parentId: CubeId = findParentIdByCubeId(cubeId);

    return isPartition(parentId);
}

// 在货架中
export function isInShelf(cubeId: CubeId): boolean {
    let parentId: CubeId = findParentIdByCubeId(cubeId);

    return isShelf(parentId);
}

// 画布
export function isSketchpad(cubeId: CubeId): boolean {
    let cube: CubeNode = findByCubeId(cubeId);

    return getCubeType(cube) === 'sketchpad';
}

// 货架
export function isShelf(cubeId: CubeId): boolean {
    let cube: CubeNode = findByCubeId(cubeId);

    return getCubeType(cube) === 'shelf';
}

// 卧柜
export function isFreezer(cubeId: CubeId): boolean {
    let cube: CubeNode = findByCubeId(cubeId);

    return getCubeType(cube) === 'freezer';
}

// 卧柜层板
export function isCabinet(cubeId: CubeId): boolean {
    let cube: CubeNode = findByCubeId(cubeId);

    return getCubeType(cube) === 'cabinet';
}

// 层板
export function isLayer(cubeId: CubeId): boolean {
    let cube: CubeNode = findByCubeId(cubeId);

    return getCubeType(cube) === 'layer';
}

// 顶板
export function isRoof(cubeId: CubeId): boolean {
    let cube: CubeNode = findByCubeId(cubeId);

    return getCubeType(cube) === 'roof';
}

// 横杆
export function isBeam(cubeId: CubeId): boolean {
    let cube: CubeNode = findByCubeId(cubeId);

    return getCubeType(cube) === 'beam';
}

// 碗碟架
export function isDishrack(cubeId: CubeId): boolean {
    let cube: CubeNode = findByCubeId(cubeId);

    return getCubeType(cube) === 'dishrack';
}

// 商品
export function isGoods(cubeId: CubeId): boolean {
    let cube: CubeNode = findByCubeId(cubeId);

    return getCubeType(cube) === 'goods';
}

// 隔板
export function isBafflePlate(cubeId: CubeId): boolean {
    let cube: CubeNode = findByCubeId(cubeId);

    return getCubeType(cube) === 'bafflePlate';
}

// 文本框
export function isTextarea(cubeId: CubeId): boolean {
    let cube: CubeNode = findByCubeId(cubeId);

    return getCubeType(cube) === 'textarea';
}

// 层板类型
export function isPartition(cubeId: CubeId): boolean {
    return isDishrack(cubeId)
        || isBeam(cubeId)
        || isRoof(cubeId)
        || isLayer(cubeId)
        || isCabinet(cubeId);
}

// 可放置组件的层板
export function isCanDropPartition(cubeId: CubeId): boolean {
    return isDishrack(cubeId)
        || isBeam(cubeId)
        || isLayer(cubeId)
        || isCabinet(cubeId);
}

// 立式货架层板类型
export function isShelfPartition(cubeId: CubeId): boolean {
    return isDishrack(cubeId)
        || isBeam(cubeId)
        || isRoof(cubeId)
        || isLayer(cubeId);
}

// 是否可放置商品
export function canDropGoods(cubeId: CubeId): boolean {
    return isDishrack(cubeId)
        || isBeam(cubeId)
        || isLayer(cubeId)
        || isSketchpad(cubeId)
        || isCabinet(cubeId);
}

// 是否可放置隔板
export function canDropBafflePlate(cubeId: CubeId): boolean {
    return isDishrack(cubeId)
        || isLayer(cubeId)
        || isSketchpad(cubeId);
}

// 是否是卧柜图纸
export function isFreezerTree(): boolean {
    let cabinet: Array<CubeId> = getCubeByType('cabinet');

    return cabinet.length !== 0;
}

// 是否是空货架
export function isEmptyShelf(cubeId: CubeId): boolean {
    if (!isShelf(cubeId) && !isFreezer(cubeId)) {
        return false;
    }

    let childIds: Array<CubeId> = findChildCubeIds(cubeId);

    return childIds.length === 0;
}

// 是否是空层板
export function isEmptyLayer(cubeId: CubeId): boolean {
    if (!isPartition(cubeId)) {
        return false;
    }

    let childIds: Array<CubeId> = findChildCubeIds(cubeId);

    return childIds.length === 0;
}

export function isPartitionList(list: CubeId[]): boolean {
    if (list.length === 0) {
        return false;
    }

    for (let i = 0; i < list.length; i++) {
        if (!isPartition(list[i])) {
            return false;
        }
    }

    return true;
}

export function isCanDropPartitionList(list: CubeId[]): boolean {
    for (let i = 0; i < list.length; i++) {
        if (!isCanDropPartition(list[i])) {
            return false;
        }
    }

    return true;
}

export function hasSubGoods(cubeId: CubeId): boolean {
    if (!isGoods(cubeId)) {
        return false;
    }

    let subSkuKey: string = Config.get({
        moduleName: 'goods',
        key: 'keyMap.subSkuList'
    });
    let cubeNode: CubeNode = findByCubeId(cubeId);
    let subSkuList: SkuData[] = Utils.getMapValueByStringKey(cubeNode, subSkuKey);

    return subSkuList.length > 1;
}

export function subGoodsCount(cubeId: CubeId): number {
    if (!isGoods(cubeId)) {
        return 0;
    }

    let subSkuKey: string = Config.get({
        moduleName: 'goods',
        key: 'keyMap.subSkuList'
    });
    let cubeNode: CubeNode = findByCubeId(cubeId);
    let subSkuList: SkuData[] = Utils.getMapValueByStringKey(cubeNode, subSkuKey);

    return subSkuList.length - 1;
}

export function isBeamsCube(cubeId: CubeId): boolean {
    let parent: CubeId = findParentIdByCubeId(cubeId);

    return isBeam(parent);
}

export function layerIsSubDrag(cubeId: CubeId): boolean {
    if (!isCanDropPartition(cubeId)) {
        return false;
    }

    let dragModeKey: string = Config.get({
        moduleName: 'layer',
        key: 'keyMap.dragMode'
    });
    let cubeNode: CubeNode = findByCubeId(cubeId);
    let dragMode: number = Utils.getMapValueByStringKey(cubeNode, dragModeKey);

    return dragMode == 2;
}

export function isLastCabinetChild(child: CubeId): boolean {
    let parent: CubeId = findParentIdByCubeId(child);

    if (!isCabinet(parent)) {
        return false;
    }

    let children: CubeId[] = findChildCubeIds(parent);

    return children[children.length - 1] === child;
}
